/**
 * Banner Pending Approvals - Redesigned with EntityManagement design system
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import InboxIcon from '@mui/icons-material/Inbox';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import EditIcon from '@mui/icons-material/Edit';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useSnackbar } from 'notistack';
import moment from 'moment-jalaali';
import * as bannerService from '../services/banner.service';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';

import './EntityManagement.css';
import './BannerPendingApprovals.css';
import AssigneeCell from '../components/common/AssigneeCell';

type StatusFilter = '' | 'pending_approval' | 'approved' | 'rejected' | 'edit_request';

const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'همه وضعیت‌ها' },
  { value: 'pending_approval', label: 'در انتظار تایید' },
  { value: 'edit_request', label: 'درخواست ویرایش' },
  { value: 'approved', label: 'تایید شده' },
  { value: 'rejected', label: 'رد شده' },
];

const getReservationStatus = (reservation: any): StatusFilter => {
  if (reservation.currentEditRequest && reservation.currentEditRequest.status === 'pending') {
    return 'edit_request';
  }
  if (reservation.status === 'approved' || reservation.status === 'active') return 'approved';
  if (reservation.status === 'rejected') return 'rejected';
  return 'pending_approval';
};

const getStatusLabel = (status: StatusFilter): string => {
  switch (status) {
    case 'pending_approval': return 'در انتظار تایید';
    case 'edit_request': return 'درخواست ویرایش';
    case 'approved': return 'تایید شده';
    case 'rejected': return 'رد شده';
    default: return 'نامشخص';
  }
};

const getStatusTagClass = (status: StatusFilter): string => {
  switch (status) {
    case 'approved': return 'tag tag-sm tag-success';
    case 'rejected': return 'tag tag-sm tag-danger';
    case 'edit_request':
    case 'pending_approval':
    default:
      return 'tag tag-sm tag-warning';
  }
};

const BannerPendingApprovals: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('');
  const [filterSlot, setFilterSlot] = useState<string>('');

  // Stats
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveEditOpen, setApproveEditOpen] = useState(false);
  const [rejectEditOpen, setRejectEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const [currentReservation, setCurrentReservation] = useState<any | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [operationSuccess, setOperationSuccess] = useState<string | null>(null);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await bannerService.getPendingReservations();
      const all: any[] = response.docs || response.data || response || [];
      setReservations(Array.isArray(all) ? all : []);
    } catch (err: any) {
      enqueueSnackbar('خطا در بارگذاری رزروها', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { fetchReservations(); }, [fetchReservations]);

  // Derive stats from loaded list
  useEffect(() => {
    const total = reservations.length;
    const pending = reservations.filter(r => {
      const s = getReservationStatus(r);
      return s === 'pending_approval' || s === 'edit_request';
    }).length;
    const approved = reservations.filter(r => getReservationStatus(r) === 'approved').length;
    const rejected = reservations.filter(r => getReservationStatus(r) === 'rejected').length;
    setStats({ total, pending, approved, rejected });
  }, [reservations]);

  // Slot options from loaded list
  const slotOptions = Array.from(new Set(
    reservations
      .map(r => r.slotId?.slotCode || r.slotId?.name || r.slotCode)
      .filter(Boolean)
  )) as string[];

  // Client-side filter + paginate
  const filtered = reservations.filter((r) => {
    const st = getReservationStatus(r);
    if (filterStatus && st !== filterStatus) return false;

    if (filterSlot) {
      const slot = r.slotId?.slotCode || r.slotId?.name || r.slotCode || '';
      if (slot !== filterSlot) return false;
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const haystack = [
        r.reservationNumber,
        r.sellerId?.name,
        r.storeId?.name,
        r.content?.title,
        r.slotId?.name,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const totalCount = filtered.length;
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalCount);
  const pageData = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const hasActiveFilters = !!searchTerm || !!filterStatus || !!filterSlot;

  // Format helpers
  const formatDate = (d?: string): string => {
    if (!d) return 'نامشخص';
    try { return toPersianDigits(moment(d).format('jYYYY/jMM/jDD')); }
    catch { return 'نامعتبر'; }
  };

  const formatPrice = (price?: number): string => {
    if (price === undefined || price === null) return '-';
    return `${toPersianDigits(price.toLocaleString('fa-IR'))} تومان`;
  };

  const getSlotName = (r: any): string => {
    return r.slotId?.name || r.slotId?.slotCode || r.slotCode || 'نامشخص';
  };

  const getPaymentStatusLabel = (s?: string) => {
    switch (s) {
      case 'paid': return 'پرداخت شده';
      case 'pending': return 'در انتظار پرداخت';
      case 'failed': return 'پرداخت ناموفق';
      default: return 'نامشخص';
    }
  };

  const getPaymentStatusClass = (s?: string) => {
    switch (s) {
      case 'paid': return 'tag tag-sm tag-success';
      case 'pending': return 'tag tag-sm tag-warning';
      case 'failed': return 'tag tag-sm tag-danger';
      default: return 'tag tag-sm tag-inactive';
    }
  };

  // Handlers
  const handleSearch = () => { setSearchTerm(searchInput); setPage(0); };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };
  const clearFilters = () => {
    setSearchTerm(''); setSearchInput(''); setFilterStatus(''); setFilterSlot(''); setPage(0);
  };

  const openDetails = (r: any) => { setCurrentReservation(r); setDetailsOpen(true); };
  const openApprove = (r: any) => { setCurrentReservation(r); setApprovalNote(''); setOperationSuccess(null); setApproveOpen(true); };
  const openReject = (r: any) => { setCurrentReservation(r); setRejectionReason(''); setOperationSuccess(null); setRejectOpen(true); };
  const openApproveEdit = (r: any) => { setCurrentReservation(r); setApprovalNote(''); setOperationSuccess(null); setApproveEditOpen(true); };
  const openRejectEdit = (r: any) => { setCurrentReservation(r); setRejectionReason(''); setOperationSuccess(null); setRejectEditOpen(true); };
  const openCancel = (r: any) => { setCurrentReservation(r); setCancelReason(''); setOperationSuccess(null); setCancelOpen(true); };

  const closeAll = () => {
    setDetailsOpen(false);
    setApproveOpen(false);
    setRejectOpen(false);
    setApproveEditOpen(false);
    setRejectEditOpen(false);
    setCancelOpen(false);
    setCleanupOpen(false);
    setCurrentReservation(null);
  };

  const handleApprove = async () => {
    if (!currentReservation) return;
    setSubmitting(true);
    try {
      await bannerService.approveReservation(currentReservation._id);
      setOperationSuccess(`رزرو «${currentReservation.reservationNumber || ''}» تایید شد`);
      enqueueSnackbar('رزرو با موفقیت تایید شد', { variant: 'success' });
      setTimeout(() => { closeAll(); fetchReservations(); }, 1000);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'خطا در تایید', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!currentReservation || !rejectionReason.trim()) {
      enqueueSnackbar('لطفاً دلیل رد را وارد کنید', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await bannerService.rejectReservation(currentReservation._id, { rejectionReason });
      setOperationSuccess(`رزرو «${currentReservation.reservationNumber || ''}» رد شد`);
      enqueueSnackbar('رزرو با موفقیت رد شد', { variant: 'success' });
      setTimeout(() => { closeAll(); fetchReservations(); }, 1000);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'خطا در رد رزرو', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveEdit = async () => {
    if (!currentReservation) return;
    setSubmitting(true);
    try {
      await bannerService.approveEditRequest(currentReservation._id);
      setOperationSuccess('درخواست ویرایش تایید شد');
      enqueueSnackbar('درخواست ویرایش با موفقیت تایید شد', { variant: 'success' });
      setTimeout(() => { closeAll(); fetchReservations(); }, 1000);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'خطا در تایید', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectEdit = async () => {
    if (!currentReservation || !rejectionReason.trim()) {
      enqueueSnackbar('لطفاً دلیل رد را وارد کنید', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await bannerService.rejectEditRequest(currentReservation._id, { reason: rejectionReason });
      setOperationSuccess('درخواست ویرایش رد شد');
      enqueueSnackbar('درخواست ویرایش با موفقیت رد شد', { variant: 'success' });
      setTimeout(() => { closeAll(); fetchReservations(); }, 1000);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'خطا در رد درخواست', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!currentReservation) return;
    setSubmitting(true);
    try {
      await bannerService.cancelReservation(currentReservation._id, { reason: cancelReason || undefined });
      setOperationSuccess('رزرو لغو شد');
      enqueueSnackbar('رزرو با موفقیت لغو شد', { variant: 'success' });
      setTimeout(() => { closeAll(); fetchReservations(); }, 1000);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'خطا در لغو رزرو', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCleanup = async () => {
    setSubmitting(true);
    try {
      const result = await bannerService.cleanupStaleReservations();
      const data = (result as any).data || result;
      const total = (data.expiredDrafts || 0) + (data.expiredPayments || 0) + (data.expiredActive || 0);
      setOperationSuccess(
        total > 0
          ? `${toPersianDigits(String(total))} رزرو پاکسازی شد`
          : 'هیچ رزرو معلقی یافت نشد'
      );
      enqueueSnackbar(
        total > 0
          ? `${total} رزرو پاکسازی شد (${data.expiredDrafts || 0} پیش‌نویس، ${data.expiredPayments || 0} پرداخت معلق، ${data.expiredActive || 0} منقضی)`
          : 'هیچ رزرو معلقی یافت نشد',
        { variant: total > 0 ? 'success' : 'info' }
      );
      setTimeout(() => { closeAll(); if (total > 0) fetchReservations(); }, 1200);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'خطا در پاکسازی', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="entity-page">
      <MyShiftStatusBanner category="banner_approval" />

      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">تأیید بنرها</h1>
          <p className="entity-subtitle">بررسی و تأیید درخواست‌های بنر فروشندگان</p>
        </div>
        <div className="header-actions">
          <button
            className="header-btn"
            onClick={() => { setOperationSuccess(null); setCleanupOpen(true); }}
            title="پاکسازی رزروهای معلق"
            disabled={submitting}
          >
            <DeleteSweepIcon fontSize="small" />
          </button>
          <button className="header-btn refresh" onClick={fetchReservations} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><CampaignIcon /></div>
          <div>
            <p className="entity-stat-label">کل درخواست‌ها</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.total))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><HourglassEmptyIcon /></div>
          <div>
            <p className="entity-stat-label">در انتظار تایید</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.pending))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">تایید شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.approved))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">رد شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.rejected))}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی شماره رزرو، فروشنده، فروشگاه..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as StatusFilter); setPage(0); }}
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {slotOptions.length > 0 && (
            <div className="filter-group">
              <label className="filter-label">اسلات</label>
              <select
                className="filter-field"
                value={filterSlot}
                onChange={(e) => { setFilterSlot(e.target.value); setPage(0); }}
              >
                <option value="">همه اسلات‌ها</option>
                {slotOptions.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {filterStatus && (
              <span className="filter-tag">
                وضعیت: {statusOptions.find(o => o.value === filterStatus)?.label}
                <button onClick={() => setFilterStatus('')}>×</button>
              </span>
            )}
            {filterSlot && (
              <span className="filter-tag">
                اسلات: {filterSlot}
                <button onClick={() => setFilterSlot('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading && !reservations.length ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : pageData.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><InboxIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">رزروی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>بنر</th>
                  <th>فروشنده / فروشگاه</th>
                  <th>اسلات</th>
                  <th>مدت</th>
                  <th>تاریخ ارسال</th>
                  <th>وضعیت</th>
                  <th>مسئول</th>
                  <th style={{ width: 180 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {pageData.map((r) => {
                  const st = getReservationStatus(r);
                  const desktopImg = r.content?.images?.desktop;
                  return (
                    <tr key={r._id}>
                      <td>
                        <div className="entity-info-cell">
                          <div className="entity-image">
                            {desktopImg ? (
                              <img
                                src={desktopImg}
                                alt={r.reservationNumber}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }}
                              />
                            ) : (
                              <CampaignIcon />
                            )}
                          </div>
                          <div className="entity-info">
                            <span className="entity-name">{r.reservationNumber || '-'}</span>
                            {r.content?.title && <span className="entity-desc">{r.content.title}</span>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="entity-desc" style={{ display: 'block' }}>
                          {r.sellerId?.name || 'نامشخص'}
                        </span>
                        <span className="entity-desc" style={{ display: 'block', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                          {r.storeId?.name || '-'}
                        </span>
                      </td>
                      <td><span className="entity-desc">{getSlotName(r)}</span></td>
                      <td>
                        <span className="entity-desc">
                          {r.dates?.durationDays ? `${toPersianDigits(String(r.dates.durationDays))} روز` : '-'}
                        </span>
                      </td>
                      <td><span className="entity-desc">{formatDate(r.createdAt || r.dates?.startDate)}</span></td>
                      <td><span className={getStatusTagClass(st)}>{getStatusLabel(st)}</span></td>
                      <td><AssigneeCell value={(r as any).assignedTo} /></td>
                      <td>
                        <div className="actions-cell">
                          <Tooltip title="جزئیات" arrow>
                            <button className="action-btn edit" onClick={() => openDetails(r)}>
                              <VisibilityIcon />
                            </button>
                          </Tooltip>
                          {st === 'edit_request' ? (
                            <>
                              <Tooltip title="تایید ویرایش" arrow>
                                <button className="action-btn toggle active" onClick={() => openApproveEdit(r)}>
                                  <CheckCircleIcon />
                                </button>
                              </Tooltip>
                              <Tooltip title="رد ویرایش" arrow>
                                <button className="action-btn delete" onClick={() => openRejectEdit(r)}>
                                  <CancelIcon />
                                </button>
                              </Tooltip>
                            </>
                          ) : st === 'pending_approval' ? (
                            <>
                              <Tooltip title="تایید" arrow>
                                <button
                                  className="action-btn toggle active"
                                  onClick={() => openApprove(r)}
                                  disabled={r.financial?.paymentStatus !== 'paid'}
                                >
                                  <CheckCircleIcon />
                                </button>
                              </Tooltip>
                              <Tooltip title="رد" arrow>
                                <button className="action-btn delete" onClick={() => openReject(r)}>
                                  <CancelIcon />
                                </button>
                              </Tooltip>
                              <Tooltip title="لغو رزرو" arrow>
                                <button className="action-btn delete" onClick={() => openCancel(r)}>
                                  <CloseIcon />
                                </button>
                              </Tooltip>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {pageData.map((r) => {
                const st = getReservationStatus(r);
                const desktopImg = r.content?.images?.desktop;
                return (
                  <div key={r._id} className="entity-card">
                    <div className="entity-card-header">
                      <div className="entity-card-image">
                        {desktopImg ? (
                          <img src={desktopImg} alt={r.reservationNumber} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                        ) : (
                          <CampaignIcon />
                        )}
                      </div>
                      <div className="entity-card-info">
                        <div className="entity-card-name">{r.reservationNumber || '-'}</div>
                        <div className="entity-card-desc">
                          {(r.sellerId?.name || 'نامشخص') + ' - ' + (r.storeId?.name || '-')}
                        </div>
                        <div className="entity-card-desc" style={{ fontSize: 12 }}>
                          {getSlotName(r)} · {r.dates?.durationDays ? `${toPersianDigits(String(r.dates.durationDays))} روز` : '-'}
                        </div>
                      </div>
                      <div className="entity-card-status">
                        <span className={getStatusTagClass(st)}>{getStatusLabel(st)}</span>
                      </div>
                    </div>
                    <div className="entity-card-actions">
                      <button className="entity-card-btn primary" onClick={() => openDetails(r)}>
                        <VisibilityIcon fontSize="small" /> جزئیات
                      </button>
                      {st === 'edit_request' ? (
                        <>
                          <button className="entity-card-btn" onClick={() => openApproveEdit(r)}>
                            <CheckCircleIcon fontSize="small" /> تایید ویرایش
                          </button>
                          <button className="entity-card-btn danger" onClick={() => openRejectEdit(r)}>
                            <CancelIcon fontSize="small" /> رد ویرایش
                          </button>
                        </>
                      ) : st === 'pending_approval' ? (
                        <>
                          <button
                            className="entity-card-btn"
                            onClick={() => openApprove(r)}
                            disabled={r.financial?.paymentStatus !== 'paid'}
                          >
                            <CheckCircleIcon fontSize="small" /> تایید
                          </button>
                          <button className="entity-card-btn danger" onClick={() => openReject(r)}>
                            <CancelIcon fontSize="small" /> رد
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از {toPersianDigits(String(totalCount))}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>
                  {toPersianDigits(String(page + 1))} / {toPersianDigits(String(totalPages || 1))}
                </span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 0}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={closeAll} maxWidth="md" fullWidth className="bannerpending-details-dialog">
        <div className="edit-dialog-title">
          <span>جزئیات رزرو بنر</span>
          <button className="details-close" onClick={closeAll}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {currentReservation && (
            <div className="edit-form">
              {/* Banner Image Preview */}
              <div className="edit-card">
                <div className="edit-card-header">تصاویر بنر</div>
                <div className="edit-card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        دسکتاپ
                      </label>
                      {currentReservation.content?.images?.desktop ? (
                        <img
                          src={currentReservation.content.images.desktop}
                          alt="Desktop"
                          style={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--color-border)', cursor: 'zoom-in' }}
                          onClick={() => { setPreviewImage(currentReservation.content.images.desktop); setImagePreviewOpen(true); }}
                        />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 24, color: 'var(--color-text-disabled)' }}>
                          <NoPhotographyIcon style={{ fontSize: 40 }} />
                          <span style={{ fontSize: 13 }}>تصویر دسکتاپ موجود نیست</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        موبایل
                      </label>
                      {currentReservation.content?.images?.mobile ? (
                        <img
                          src={currentReservation.content.images.mobile}
                          alt="Mobile"
                          style={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--color-border)', cursor: 'zoom-in' }}
                          onClick={() => { setPreviewImage(currentReservation.content.images.mobile); setImagePreviewOpen(true); }}
                        />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 24, color: 'var(--color-text-disabled)' }}>
                          <NoPhotographyIcon style={{ fontSize: 40 }} />
                          <span style={{ fontSize: 13 }}>تصویر موبایل موجود نیست</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {(currentReservation.content?.title || currentReservation.content?.description) && (
                    <div className="edit-form-grid" style={{ marginTop: 16 }}>
                      {currentReservation.content?.title && (
                        <div className="edit-field full">
                          <label>عنوان</label>
                          <input type="text" readOnly value={currentReservation.content.title} />
                        </div>
                      )}
                      {currentReservation.content?.description && (
                        <div className="edit-field full">
                          <label>توضیحات</label>
                          <textarea readOnly value={currentReservation.content.description} rows={2} />
                        </div>
                      )}
                      {currentReservation.content?.link?.target && (
                        <div className="edit-field full">
                          <label>لینک</label>
                          <input type="text" readOnly value={currentReservation.content.link.target} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Edit Request Comparison (if applicable) */}
              {currentReservation.currentEditRequest && currentReservation.currentEditRequest.status === 'pending' && (
                <div className="edit-card">
                  <div className="edit-card-header">مقایسه محتوای ویرایش</div>
                  <div className="edit-card-body">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                          محتوای فعلی
                        </label>
                        {currentReservation.content?.images?.desktop ? (
                          <img
                            src={currentReservation.content.images.desktop}
                            alt="Current"
                            style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--color-border)' }}
                          />
                        ) : (
                          <div style={{ width: '100%', height: 180, background: 'var(--color-bg-default)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-disabled)' }}>
                            بدون تصویر
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: 'var(--color-warning-dark)', fontWeight: 600 }}>
                          محتوای درخواست شده
                        </label>
                        {currentReservation.currentEditRequest?.newContent?.images?.desktop ? (
                          <img
                            src={currentReservation.currentEditRequest.newContent.images.desktop}
                            alt="New"
                            style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 8, border: '2px solid var(--color-warning)' }}
                          />
                        ) : (
                          <div style={{ width: '100%', height: 180, background: 'var(--color-warning-bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-warning-dark)' }}>
                            بدون تصویر
                          </div>
                        )}
                      </div>
                    </div>
                    {currentReservation.currentEditRequest?.newContent?.title && (
                      <div className="edit-field full" style={{ marginTop: 12 }}>
                        <label>عنوان جدید</label>
                        <input type="text" readOnly value={currentReservation.currentEditRequest.newContent.title} />
                      </div>
                    )}
                    {currentReservation.currentEditRequest?.newContent?.description && (
                      <div className="edit-field full">
                        <label>توضیحات جدید</label>
                        <textarea readOnly value={currentReservation.currentEditRequest.newContent.description} rows={2} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Seller Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات فروشنده</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>شماره رزرو</label>
                      <input type="text" readOnly value={currentReservation.reservationNumber || '-'} />
                    </div>
                    <div className="edit-field">
                      <label>فروشنده</label>
                      <input type="text" readOnly value={currentReservation.sellerId?.name || 'نامشخص'} />
                    </div>
                    <div className="edit-field">
                      <label>فروشگاه</label>
                      <input type="text" readOnly value={currentReservation.storeId?.name || 'نامشخص'} />
                    </div>
                    <div className="edit-field">
                      <label>ایمیل فروشنده</label>
                      <input type="text" readOnly value={currentReservation.sellerId?.email || '-'} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Slot & Schedule */}
              <div className="edit-card">
                <div className="edit-card-header">اسلات و زمان‌بندی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام اسلات</label>
                      <input type="text" readOnly value={getSlotName(currentReservation)} />
                    </div>
                    <div className="edit-field">
                      <label>مدت (روز)</label>
                      <input type="text" readOnly value={toPersianDigits(String(currentReservation.dates?.durationDays || '-'))} />
                    </div>
                    <div className="edit-field">
                      <label>تاریخ شروع</label>
                      <input type="text" readOnly value={formatDate(currentReservation.dates?.startDate)} />
                    </div>
                    <div className="edit-field">
                      <label>تاریخ پایان</label>
                      <input type="text" readOnly value={formatDate(currentReservation.dates?.endDate)} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات پرداخت</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>مبلغ نهایی</label>
                      <input type="text" readOnly value={formatPrice(currentReservation.financial?.finalAmount)} />
                    </div>
                    <div className="edit-field">
                      <label>وضعیت پرداخت</label>
                      <div style={{ padding: '6px 0' }}>
                        <span className={getPaymentStatusClass(currentReservation.financial?.paymentStatus)}>
                          {getPaymentStatusLabel(currentReservation.financial?.paymentStatus)}
                        </span>
                      </div>
                    </div>
                    {currentReservation.financial?.refNumber && (
                      <div className="edit-field">
                        <label>کد پیگیری</label>
                        <input type="text" readOnly value={currentReservation.financial.refNumber} />
                      </div>
                    )}
                    {currentReservation.financial?.paidAt && (
                      <div className="edit-field">
                        <label>تاریخ پرداخت</label>
                        <input type="text" readOnly value={formatDate(currentReservation.financial.paidAt)} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Decision / Status */}
              <div className="edit-card">
                <div className="edit-card-header">وضعیت تصمیم</div>
                <div className="edit-card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>وضعیت فعلی:</span>
                      <span className={getStatusTagClass(getReservationStatus(currentReservation))}>
                        {getStatusLabel(getReservationStatus(currentReservation))}
                      </span>
                    </div>
                    {currentReservation.status === 'rejected' && currentReservation.rejectionReason && (
                      <div className="edit-field">
                        <label>دلیل رد</label>
                        <textarea readOnly value={currentReservation.rejectionReason} rows={2} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeAll}>بستن</button>
          {currentReservation && getReservationStatus(currentReservation) === 'edit_request' && (
            <>
              <button
                className="edit-btn cancel"
                style={{ color: 'var(--color-error-dark)', borderColor: 'var(--color-error-dark)' }}
                onClick={() => { setDetailsOpen(false); openRejectEdit(currentReservation); }}
              >
                <CancelIcon fontSize="small" /> رد ویرایش
              </button>
              <button
                className="edit-btn submit"
                style={{ background: 'var(--color-success-dark)' }}
                onClick={() => { setDetailsOpen(false); openApproveEdit(currentReservation); }}
              >
                <CheckCircleIcon fontSize="small" /> تایید ویرایش
              </button>
            </>
          )}
          {currentReservation && getReservationStatus(currentReservation) === 'pending_approval' && (
            <>
              <button
                className="edit-btn cancel"
                style={{ color: 'var(--color-error-dark)', borderColor: 'var(--color-error-dark)' }}
                onClick={() => { setDetailsOpen(false); openReject(currentReservation); }}
              >
                <CancelIcon fontSize="small" /> رد رزرو
              </button>
              <button
                className="edit-btn submit"
                style={{ background: 'var(--color-success-dark)' }}
                onClick={() => { setDetailsOpen(false); openApprove(currentReservation); }}
                disabled={currentReservation.financial?.paymentStatus !== 'paid'}
              >
                <CheckCircleIcon fontSize="small" /> تایید رزرو
              </button>
            </>
          )}
        </div>
      </Dialog>

      {/* Approve Confirm Dialog */}
      <Dialog open={approveOpen} onClose={closeAll} maxWidth="xs" fullWidth className="bannerpending-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon" style={{ background: 'var(--color-success-bg)' }}>
            <CheckCircleIcon style={{ fontSize: 28, color: 'var(--color-success-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">تایید رزرو بنر</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-success-dark)' }}>
              <CheckCircleIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <>
              <p className="delete-dialog-text">
                آیا از تایید رزرو <strong>«{currentReservation?.reservationNumber}»</strong> اطمینان دارید؟
                <br />با تایید، بنر در زمان مقرر منتشر می‌شود.
              </p>
              <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
                <textarea
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  placeholder="یادداشت (اختیاری)..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    background: 'var(--color-bg-paper)',
                    color: 'var(--color-text-primary)'
                  }}
                />
              </div>
            </>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={submitting}>انصراف</button>
            <button
              className="btn btn-danger"
              style={{ background: 'var(--color-success-dark)' }}
              onClick={handleApprove}
              disabled={submitting || !!operationSuccess}
            >
              {submitting && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <CheckCircleIcon fontSize="small" /> تایید
            </button>
          </div>
        </div>
      </Dialog>

      {/* Reject Confirm Dialog */}
      <Dialog open={rejectOpen} onClose={closeAll} maxWidth="xs" fullWidth className="bannerpending-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon">
            <CancelIcon style={{ fontSize: 28, color: 'var(--color-error-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">رد رزرو بنر</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-error-dark)' }}>
              <CancelIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <>
              <p className="delete-dialog-text">
                دلیل رد رزرو <strong>«{currentReservation?.reservationNumber}»</strong> را وارد کنید.
                <br />این دلیل به فروشنده نمایش داده می‌شود.
              </p>
              <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="دلیل رد را وارد کنید..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${!rejectionReason.trim() ? 'var(--color-error-dark)' : 'var(--color-border)'}`,
                    borderRadius: 8,
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    background: 'var(--color-bg-paper)',
                    color: 'var(--color-text-primary)'
                  }}
                />
                {!rejectionReason.trim() && (
                  <span style={{ color: 'var(--color-error-dark)', fontSize: 12, marginTop: 4, display: 'block' }}>
                    وارد کردن دلیل الزامی است
                  </span>
                )}
              </div>
            </>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={submitting}>انصراف</button>
            <button
              className="btn btn-danger"
              onClick={handleReject}
              disabled={submitting || !rejectionReason.trim() || !!operationSuccess}
            >
              {submitting && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <CancelIcon fontSize="small" /> رد رزرو
            </button>
          </div>
        </div>
      </Dialog>

      {/* Approve Edit Request Confirm Dialog */}
      <Dialog open={approveEditOpen} onClose={closeAll} maxWidth="xs" fullWidth className="bannerpending-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon" style={{ background: 'var(--color-success-bg)' }}>
            <EditIcon style={{ fontSize: 28, color: 'var(--color-success-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">تایید درخواست ویرایش</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-success-dark)' }}>
              <CheckCircleIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <p className="delete-dialog-text">
              آیا از تایید درخواست ویرایش رزرو <strong>«{currentReservation?.reservationNumber}»</strong> اطمینان دارید؟
              <br />محتوای جدید جایگزین محتوای فعلی می‌شود.
            </p>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={submitting}>انصراف</button>
            <button
              className="btn btn-danger"
              style={{ background: 'var(--color-success-dark)' }}
              onClick={handleApproveEdit}
              disabled={submitting || !!operationSuccess}
            >
              {submitting && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <CheckCircleIcon fontSize="small" /> تایید ویرایش
            </button>
          </div>
        </div>
      </Dialog>

      {/* Reject Edit Request Confirm Dialog */}
      <Dialog open={rejectEditOpen} onClose={closeAll} maxWidth="xs" fullWidth className="bannerpending-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon">
            <CancelIcon style={{ fontSize: 28, color: 'var(--color-error-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">رد درخواست ویرایش</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-error-dark)' }}>
              <CancelIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <>
              <p className="delete-dialog-text">
                دلیل رد درخواست ویرایش <strong>«{currentReservation?.reservationNumber}»</strong> را وارد کنید.
              </p>
              <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="دلیل رد را وارد کنید..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${!rejectionReason.trim() ? 'var(--color-error-dark)' : 'var(--color-border)'}`,
                    borderRadius: 8,
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    background: 'var(--color-bg-paper)',
                    color: 'var(--color-text-primary)'
                  }}
                />
                {!rejectionReason.trim() && (
                  <span style={{ color: 'var(--color-error-dark)', fontSize: 12, marginTop: 4, display: 'block' }}>
                    وارد کردن دلیل الزامی است
                  </span>
                )}
              </div>
            </>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={submitting}>انصراف</button>
            <button
              className="btn btn-danger"
              onClick={handleRejectEdit}
              disabled={submitting || !rejectionReason.trim() || !!operationSuccess}
            >
              {submitting && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <CancelIcon fontSize="small" /> رد ویرایش
            </button>
          </div>
        </div>
      </Dialog>

      {/* Cancel Reservation Confirm Dialog */}
      <Dialog open={cancelOpen} onClose={closeAll} maxWidth="xs" fullWidth className="bannerpending-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon">
            <CloseIcon style={{ fontSize: 28, color: 'var(--color-error-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">لغو کامل رزرو</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-error-dark)' }}>
              <CheckCircleIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <>
              <p className="delete-dialog-text">
                آیا از لغو رزرو <strong>«{currentReservation?.reservationNumber}»</strong> اطمینان دارید؟
              </p>
              <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="دلیل لغو (اختیاری)..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    background: 'var(--color-bg-paper)',
                    color: 'var(--color-text-primary)'
                  }}
                />
              </div>
            </>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={submitting}>انصراف</button>
            <button
              className="btn btn-danger"
              onClick={handleCancel}
              disabled={submitting || !!operationSuccess}
            >
              {submitting && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <CloseIcon fontSize="small" /> لغو رزرو
            </button>
          </div>
        </div>
      </Dialog>

      {/* Cleanup Confirm Dialog */}
      <Dialog open={cleanupOpen} onClose={closeAll} maxWidth="xs" fullWidth className="bannerpending-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon" style={{ background: 'var(--color-warning-bg)' }}>
            <DeleteSweepIcon style={{ fontSize: 28, color: 'var(--color-warning-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">پاکسازی رزروهای معلق</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-success-dark)' }}>
              <CheckCircleIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <p className="delete-dialog-text">
              آیا از پاکسازی رزروهای معلق، منقضی و پیش‌نویس‌های قدیمی اطمینان دارید؟
              <br />این عملیات رزروهای بدون فعالیت را حذف می‌کند.
            </p>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={submitting}>انصراف</button>
            <button
              className="btn btn-danger"
              style={{ background: 'var(--color-warning-dark)' }}
              onClick={handleCleanup}
              disabled={submitting || !!operationSuccess}
            >
              {submitting && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <DeleteSweepIcon fontSize="small" /> پاکسازی
            </button>
          </div>
        </div>
      </Dialog>

      {/* Image Preview Overlay */}
      {imagePreviewOpen && previewImage && (
        <div
          onClick={() => setImagePreviewOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <button
            onClick={() => setImagePreviewOpen(false)}
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon />
          </button>
          <img
            src={previewImage}
            alt="پیش‌نمایش"
            style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default BannerPendingApprovals;
